import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "../lib/api.ts";
import { useSession } from "../lib/session.ts";
import { AppleSignInButton } from "./AppleSignInButton.tsx";
import { GoogleSignInButton } from "./GoogleSignInButton.tsx";
import { isTurkishIos } from "../lib/native.ts";
import { PasswordInput } from "./PasswordInput.tsx";
import { Sheet } from "./Sheet.tsx";
import { messages } from "../i18n/index.ts";

interface DeleteAccountSheetProps {
  open: boolean;
  onClose: () => void;
  /** Silme başarılıysa çağrılır — ekrandan çıkmak için. */
  onDeleted: () => void;
}

/**
 * Hesap silme onayı.
 *
 * Geri alınamayan bir işlem olduğu için iki eşik var: kullanıcı adını yazmak ve
 * kimliğini yeniden kanıtlamak. Şifresi olan hesap şifresini girer; Google ya
 * da Apple ile açılmış hesabın şifresi yok, o yüzden sağlayıcıya yeniden onaylatır.
 * Apple doğrulaması yalnızca iOS uygulamasında var.
 */
export function DeleteAccountSheet({ open, onClose, onDeleted }: DeleteAccountSheetProps) {
  const username = useSession((state) => state.user?.username ?? "");
  const account = useSession((state) => state.account);
  const deleteAccount = useSession((state) => state.deleteAccount);
  const [password, setPassword] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Hesap bilgisi henüz gelmediyse şifreli varsayıyoruz: yaygın olan o.
  const usesPassword = account?.hasPassword ?? true;

  useEffect(() => {
    if (!open) return;
    setPassword("");
    setConfirmName("");
    setError(null);
    setBusy(false);
  }, [open]);

  const nameMatches = confirmName.trim() === username;
  const canSubmit = !busy && nameMatches && (!usesPassword || password.length > 0);

  async function run(proof: { password?: string; credential?: string; appleIdentityToken?: string }) {
    setBusy(true);
    setError(null);
    try {
      await deleteAccount(proof);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : messages.deleteAccount.failed);
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || !usesPassword) return;
    await run({ password });
  }

  return (
    <Sheet
      open={open}
      title={messages.deleteAccount.title}
      accent="flame"
      placement="center"
      onClose={busy ? () => undefined : onClose}
    >
      <p className="text-sm leading-relaxed text-muted">
        {messages.deleteAccount.description}
      </p>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
        {usesPassword && (
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted">
              {messages.deleteAccount.password}
            </span>
            <PasswordInput
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 pr-11 text-base"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={busy}
            />
          </label>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold uppercase tracking-wider text-muted">
            {messages.deleteAccount.confirmName(username)}
          </span>
          <input
            type="text"
            className="rounded-lg border border-line bg-surface px-3 py-2 text-base"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            autoCapitalize="none"
            autoComplete="off"
            disabled={busy}
          />
        </label>

        {error && (
          <p className="text-sm text-flame" role="alert">
            {error}
          </p>
        )}

        {!usesPassword && account?.hasApple && isTurkishIos() && (
          <div className="mt-1">
            <p className="mb-2 text-xs leading-relaxed text-muted">
              {messages.deleteAccount.appleProof}
            </p>
            <AppleSignInButton
              disabled={busy || !nameMatches}
              onCredential={({ identityToken }) => void run({ appleIdentityToken: identityToken })}
            />
          </div>
        )}

        {!usesPassword && account?.hasApple && !account.hasGoogle && !isTurkishIos() && (
          <p className="mt-1 text-xs leading-relaxed text-muted">
            {messages.deleteAccount.appleOnlyElsewhere}
          </p>
        )}

        {!usesPassword && account?.hasGoogle !== false && (
          <div className="mt-1">
            <p className="mb-2 text-xs leading-relaxed text-muted">
              {messages.deleteAccount.googleProof}
            </p>
            <GoogleSignInButton
              text="continue_with"
              disabled={busy || !nameMatches}
              onCredential={(credential) => void run({ credential })}
            />
          </div>
        )}

        <div className="mt-2 flex gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            {messages.common.cancel}
          </button>
          {usesPassword && (
            <button type="submit" className="btn-primary btn-primary--flame" disabled={!canSubmit}>
              {busy ? messages.deleteAccount.deleting : messages.deleteAccount.deleteForever}
            </button>
          )}
        </div>
      </form>
    </Sheet>
  );
}
